import React from "react"
import toTitleCase from "../Functions/toTitleCase"

// Shared so the Add and Edit pricing modals label the same car model
// identically -- an edit that re-picked a model from a differently-worded
// list would look like a different model to whoever reviews the price.
export const carModelOptionLabel = (carModel) => (
    toTitleCase(carModel.make || "") + " " + carModel.name
    + (carModel.series ? " | " + carModel.series : "")
    + (carModel.groupType ? " | " + carModel.groupType.name : " | No Group Selected")
)

export const carModelShortLabel = (carModel) => (
    `${toTitleCase(carModel.make || "")} ${toTitleCase(carModel.name)}`.trim()
)

// One model per line for a title attribute. The listing cell only has room for
// the first model and only shows that one's group, so when a template covers
// several this tooltip is the only place the full set -- and each model's own
// group -- is visible.
export const carModelTooltipLine = (carModel) => (
    carModelShortLabel(carModel)
    + (carModel.series ? ` (${carModel.series})` : "")
    + " — " + (carModel.groupType ? carModel.groupType.name : "No group")
)

export const carModelTooltip = (carModels) => carModels.map(carModelTooltipLine).join("\n")

// The listing shows one group line for a whole template, but a template can
// cover models from different groups. Naming only the first model's group
// reads as a fact about the template when it is not one.
export const carModelGroupNames = (carModels) => [
    ...new Set(carModels.map(carModel => (carModel.groupType ? carModel.groupType.name : "No group")))
]

export const carModelGroupSummary = (groupNames) => {
    if (groupNames.length === 0) return "--"
    // A lone "No group" stays "--", the table's usual empty marker. It only
    // earns words once there is something to contrast it with.
    if (groupNames.length === 1) return groupNames[0] === "No group" ? "--" : groupNames[0]
    if (groupNames.length === 2) return groupNames.join(", ")
    return `${groupNames[0]}, ${groupNames[1]} +${groupNames.length - 2}`
}

export const carModelOption = (carModel) => ({
    value: carModel.id,
    label: carModelOptionLabel(carModel),
    // Carried alongside the long option label so summaries can show a readable
    // name without the series and group-type text the picker needs to tell two
    // same-named models apart. react-select ignores keys it does not know.
    shortLabel: carModelShortLabel(carModel),
})

export const StatusChip = ({ on, onText, offText }) => (
    <span className={"listing-chip " + (on ? "listing-chip--on" : "listing-chip--off")}>
        <i className={"fa " + (on ? "fa-check" : "fa-times")} aria-hidden="true"></i>
        {on ? onText : offText}
    </span>
)

export const VisibilityChip = ({ visible }) => (
    <span className={"listing-chip listing-chip--visibility " + (visible ? "listing-chip--on" : "listing-chip--off")}>
        <i className={"fa " + (visible ? "fa-eye" : "fa-eye-slash")} aria-hidden="true"></i>
        {visible ? "Visible" : "Hidden"}
    </span>
)

export const formatDateRange = (startIso, endIso) => {
    if (!startIso || !endIso) return "--"
    const options = { month: "short", day: "numeric" }
    const start = new Date(startIso + "T00:00:00")
    const end = new Date(endIso + "T00:00:00")
    return `${start.toLocaleDateString("en-US", options)} – ${end.toLocaleDateString("en-US", options)}, ${end.getFullYear()}`
}
